import { zodResolver } from '@hookform/resolvers/zod';
import {
  getPostApiCertificationProgramMutationOptions,
  NormalizedApiError,
  useGetApiCertificationProgramList,
  useGetApiInstitution,
  useGetApiLab,
  type CertificationProgramCreateDTO,
} from '@smartsash/api-client';
import { useEntityCreate } from '@smartsash/server-ops';
import { Input, Select, type SelectOption } from '@smartsash/ui';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import styles from './CertificationCreateForm.module.css';

import { LOOKUP_PAGE_SIZE } from '@/hooks/useLookupNames';

/** The endpoint's own bounds, restated because no write DTO declares a `required` array. */
const VALIDITY_DAYS_MIN = 1;
const PASSING_SCORE_MIN = 0;
const PASSING_SCORE_MAX = 100;

/** What a fresh programme starts as, matching the values the design shows. */
const DEFAULT_VALIDITY_DAYS = 365;
const DEFAULT_PASSING_SCORE = 100;

/**
 * Every programme is created switched off (HS-1464).
 *
 * `POST` takes no items — the structure is authored afterwards through `PUT` — so a programme is
 * empty for as long as it takes someone to add a question. An empty active programme is a state
 * the server's own rule already treats as no programme at all:
 * `CertificationContextResolver.ResolveProgramAsync` keeps only candidates with at least one
 * visible item. Offering `active` here was therefore offering a value that could not be true yet,
 * which is why the status control is a hint line rather than a `Select`. The editor's power button
 * switches the programme on, once there is something to switch on.
 */
const CREATED_STATUS = 'inactive';

/** The one status a duplicate `labId` comes back as. */
const STATUS_CONFLICT = 409;

/**
 * The create form's own schema.
 *
 * The generated Zod schema cannot stand in for this: `CertificationProgramCreateDTO` declares no
 * `required` array, so codegen marks every field optional and none of the server's real bounds
 * survive into the client. They are restated here, which is also the only place they can produce a
 * field-level message.
 */
function useCreateSchema() {
  const { t } = useTranslation();
  return useMemo(
    () =>
      z.object({
        institutionId: z.string().min(1, t('certifications.form.instituteRequired')),
        // Allowed to stay empty: a programme without a laboratory covers the whole institution,
        // and the create body spells that as `labId: null`.
        labId: z.string(),
        validityDays: z
          .number({ message: t('certifications.form.validityDaysInvalid') })
          .int(t('certifications.form.validityDaysInvalid'))
          .min(VALIDITY_DAYS_MIN, t('certifications.form.validityDaysInvalid')),
        passingScore: z
          .number({ message: t('certifications.form.passingScoreInvalid') })
          .int(t('certifications.form.passingScoreInvalid'))
          .min(PASSING_SCORE_MIN, t('certifications.form.passingScoreInvalid'))
          .max(PASSING_SCORE_MAX, t('certifications.form.passingScoreInvalid')),
      }),
    [t],
  );
}

type CreateValues = z.infer<ReturnType<typeof useCreateSchema>>;

export interface CertificationCreateFormProps {
  /**
   * The `<form>` element's id. The dialog's own footer button submits through
   * `requestSubmit()` on this id, because a `ModalAction` cannot be a `type="submit"` button.
   */
  formId: string;
  /** Called once the server has created the programme. */
  onCreated: (id: string) => void;
}

/**
 * Create a certification programme (HS-1205, first slice; institution scope added HS-1366).
 *
 * The programme is created **empty** — the body carries the institution, an optional laboratory,
 * the validity period and the passing score, and `items[]` is authored afterwards through `PUT`.
 * That is the contract's own two-phase shape, not a simplification: `POST` does not accept items
 * at all. The status is not a choice: it is always `inactive` (HS-1464, see `CREATED_STATUS`).
 *
 * **The institution owns the programme; the laboratory narrows it.** `CertificationProgramCreateDTO`
 * carries `institutionId`, and `labId` is nullable. Scope is derived from `labId` alone — the body
 * has no separate scope field — so a laboratory left unchosen is submitted as an explicit `null`,
 * which the server reads as a programme covering the whole institution.
 *
 * A laboratory may own at most one programme, so laboratories that already have one are listed but
 * disabled, with the reason beside the name. An institution carries no such affordance: the
 * uniqueness rule for institution-scoped programmes is not documented, and a wrong `disabled` would
 * be worse than none. Either way an affordance is not a guarantee — two administrators can still
 * race — so a `409` is caught and rendered on whichever field the chosen scope makes responsible.
 * It has to be recognised by **status**: `NormalizedApiError` drops the `parameterName` the
 * response carries, and every non-401/403/404 4xx normalises to the same `errors.validation`
 * message key.
 *
 * **Neither picker is `searchable`, which departs from `apps/admin/CLAUDE.md`.** That guidance
 * ("pass `searchable` when the list grows with the data — institutions, labs, groups") is sound,
 * and the developer asked for plain lists here anyway: both vocabularies are short today, and a
 * type-to-filter input on a six-item list is a text box standing between the user and six visible
 * options. Revisit when either list outgrows a single glance — the change is one prop per select.
 */
export function CertificationCreateForm({ formId, onCreated }: CertificationCreateFormProps) {
  const { t } = useTranslation();
  const schema = useCreateSchema();

  const { data: institutionData } = useGetApiInstitution({ page: 1, pageSize: LOOKUP_PAGE_SIZE });
  const { data: labData } = useGetApiLab({ page: 1, pageSize: LOOKUP_PAGE_SIZE });
  const { data: programmeData } = useGetApiCertificationProgramList({
    page: 1,
    pageSize: LOOKUP_PAGE_SIZE,
  });

  const create = useEntityCreate({
    entity: 'certificationProgram',
    mutationOptions: getPostApiCertificationProgramMutationOptions,
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm<CreateValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      institutionId: '',
      labId: '',
      validityDays: DEFAULT_VALIDITY_DAYS,
      passingScore: DEFAULT_PASSING_SCORE,
    },
  });

  const institutionId = watch('institutionId');

  const institutionOptions = useMemo<SelectOption[]>(
    () =>
      (institutionData?.items ?? []).map((institution) => ({
        value: institution.id ?? '',
        label: institution.name ?? '',
      })),
    [institutionData],
  );

  /** Laboratories that already own a programme, so the picker can say so instead of failing. */
  const takenLabIds = useMemo(
    () =>
      new Set(
        (programmeData?.items ?? [])
          .map((programme) => programme.labId)
          .filter((id): id is string => id != null),
      ),
    [programmeData],
  );

  /**
   * Empty until an institution is chosen: a laboratory list only means something inside one
   * institution, and the leading option is the institution-scoped choice itself.
   */
  const labOptions = useMemo<SelectOption[]>(() => {
    if (institutionId === '') return [];
    return [
      { value: '', label: t('certifications.form.wholeInstitute') },
      ...(labData?.items ?? [])
        .filter((lab) => lab.institutionId === institutionId)
        .map((lab) => {
          const taken = takenLabIds.has(lab.id ?? '');
          return {
            value: lab.id ?? '',
            label: lab.name ?? '',
            disabled: taken,
            supportingText: taken ? t('certifications.form.labAlreadyHasProgramme') : undefined,
          };
        }),
    ];
  }, [labData, institutionId, takenLabIds, t]);

  const onSubmit = handleSubmit(async (values) => {
    const labId = values.labId === '' ? null : values.labId;
    const payload: CertificationProgramCreateDTO = {
      institutionId: values.institutionId,
      labId,
      validityDays: values.validityDays,
      passingScore: values.passingScore,
      status: CREATED_STATUS,
    };
    try {
      const created = await create.mutateAsync({ data: payload });
      onCreated(created.id ?? '');
    } catch (error) {
      if (error instanceof NormalizedApiError && error.status === STATUS_CONFLICT) {
        // The conflict belongs to whichever scope was asked for: a duplicate laboratory when one
        // was chosen, a duplicate institution-wide programme when it was not.
        if (labId == null) {
          setError('institutionId', {
            message: t('certifications.form.instituteAlreadyHasProgramme'),
          });
        } else {
          setError('labId', { message: t('certifications.form.labAlreadyHasProgramme') });
        }
        return;
      }
      setError('root', { message: t('certifications.form.createFailed') });
    }
  });

  return (
    <form
      id={formId}
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        void onSubmit(event);
      }}
    >
      <Controller
        name="institutionId"
        control={control}
        render={({ field }) => (
          <Select
            label={t('certifications.form.institute')}
            placeholder={t('certifications.form.institutePlaceholder')}
            options={institutionOptions}
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              // The chosen laboratory belongs to the previous institution; keeping it would
              // submit a laboratory the new institution does not own.
              setValue('labId', '');
            }}
            errorMessage={errors.institutionId?.message}
            required
          />
        )}
      />

      <Controller
        name="labId"
        control={control}
        render={({ field }) => (
          <Select
            label={t('certifications.form.laboratory')}
            hintText={t('certifications.form.laboratoryHint')}
            placeholder={t('certifications.form.laboratoryPlaceholder')}
            options={labOptions}
            value={field.value}
            onChange={field.onChange}
            errorMessage={errors.labId?.message}
            disabled={institutionId === ''}
          />
        )}
      />

      <div className={styles.row}>
        <Input
          type="number"
          label={t('certifications.columns.validityDays')}
          min={VALIDITY_DAYS_MIN}
          errorMessage={errors.validityDays?.message}
          {...register('validityDays', { valueAsNumber: true })}
        />
        <Input
          type="number"
          label={t('certifications.form.percentageToPass')}
          min={PASSING_SCORE_MIN}
          max={PASSING_SCORE_MAX}
          errorMessage={errors.passingScore?.message}
          {...register('passingScore', { valueAsNumber: true })}
        />
      </div>

      <p className={styles.hint}>{t('certifications.form.createdInactiveHint')}</p>

      {errors.root?.message != null && (
        <p role="alert" className={styles.error}>
          {errors.root.message}
        </p>
      )}
    </form>
  );
}

export default CertificationCreateForm;
